/**
 * Pure enumeration of a dicom-parser DataSet into display rows for the
 * DICOM tags dialog. Written against a minimal structural interface so
 * tests can pass synthetic object literals instead of parsing real files.
 *
 * The `<binary, N bytes>` / `(empty)` value literals are intentionally
 * untranslated: they are DICOM-domain notation, and keeping them here
 * keeps this module i18n-free and pure.
 */
const MAX_VALUE_LENGTH = 128;
const BINARY_VRS = new Set(['OB', 'OW', 'OL', 'OV', 'OD', 'OF', 'UN']);
const PIXEL_DATA_HEX = '7fe00010';
/** VRs whose value bytes are encoded per Specific Character Set (0008,0005).
 * All other string VRs use the default (ASCII) repertoire by definition. */
const CHARSET_TEXT_VRS = new Set(['SH', 'LO', 'ST', 'LT', 'PN', 'UT', 'UC']);
/** DICOM defined terms → WHATWG TextDecoder labels. Only single-value
 * charsets TextDecoder can handle; ISO 2022 code extensions (multi-valued
 * 0008,0005) fall back to dicom-parser's byte-per-char reading. */
const CHARSET_LABELS = {
    'ISO_IR 100': 'iso-8859-1',
    'ISO_IR 101': 'iso-8859-2',
    'ISO_IR 109': 'iso-8859-3',
    'ISO_IR 110': 'iso-8859-4',
    'ISO_IR 144': 'iso-8859-5',
    'ISO_IR 127': 'iso-8859-6',
    'ISO_IR 126': 'iso-8859-7',
    'ISO_IR 138': 'iso-8859-8',
    'ISO_IR 148': 'iso-8859-9',
    'ISO_IR 166': 'windows-874',
    'ISO_IR 192': 'utf-8',
    GB18030: 'gb18030',
    GBK: 'gbk',
};
const NUMERIC_VRS = {
    US: { bytes: 2, method: 'uint16' },
    SS: { bytes: 2, method: 'int16' },
    UL: { bytes: 4, method: 'uint32' },
    SL: { bytes: 4, method: 'int32' },
    FL: { bytes: 4, method: 'float' },
    FD: { bytes: 8, method: 'double' },
};
/** 'xggggeeee' → lowercase 'ggggeeee'. */
function tagHex(elementTag) {
    return elementTag.replace(/^x/, '').toLowerCase();
}
/** 'ggggeeee' → '(GGGG,EEEE)'. */
function displayTag(hex) {
    return `(${hex.slice(0, 4).toUpperCase()},${hex.slice(4, 8).toUpperCase()})`;
}
function isPrivateGroup(hex) {
    return (parseInt(hex.slice(0, 4), 16) & 1) === 1;
}
function binaryPlaceholder(byteLength) {
    return `<binary, ${byteLength} bytes>`;
}
function formatNumericValue(ds, el, vr) {
    const spec = NUMERIC_VRS[vr];
    if (!spec)
        return null;
    const reader = ds[spec.method];
    if (!reader)
        return null;
    const count = Math.floor(el.length / spec.bytes);
    const values = [];
    for (let i = 0; i < count; i++) {
        // dicom-parser's readers are prototype methods that use `this.elements` —
        // they must be invoked with the dataset as receiver.
        const v = reader.call(ds, el.tag, i);
        if (v === undefined)
            return null;
        values.push(String(v));
    }
    return values.join('\\');
}
/** AT elements hold packed (group, element) uint16 pairs. */
function formatAttributeTagValue(ds, el) {
    if (!ds.uint16)
        return null;
    const count = Math.floor(el.length / 4);
    const values = [];
    for (let i = 0; i < count; i++) {
        const group = ds.uint16(el.tag, i * 2);
        const element = ds.uint16(el.tag, i * 2 + 1);
        if (group === undefined || element === undefined)
            return null;
        values.push(`(${group.toString(16).padStart(4, '0').toUpperCase()},${element
            .toString(16)
            .padStart(4, '0')
            .toUpperCase()})`);
    }
    return values.join('\\');
}
/** Decoder for this dataset's Specific Character Set: its own 0008,0005 if
 * present (a sequence item's declaration replaces the inherited one),
 * otherwise `whenAbsent`. Null → let dicom-parser read byte-per-char, which
 * is what an unsupported declaration (ISO 2022 code extensions) falls back to. */
function resolveDecoder(ds, whenAbsent) {
    if (!ds.elements.x00080005)
        return whenAbsent;
    // 0008,0005 is CS — always ASCII, so reading it byte-per-char is safe.
    const declared = ds.string?.('x00080005')?.trim();
    const label = declared ? CHARSET_LABELS[declared] : undefined;
    if (!label)
        return null;
    try {
        return new TextDecoder(label);
    }
    catch {
        return null;
    }
}
/** Mirror dicom-parser's string(): a value ends at its first NUL, and the
 * remainder is trimmed. Cutting at the NUL rather than stripping a trailing run
 * of them matters for the real padding senders emit — a NUL-terminated value
 * padded to even length ends in a space, so `\0+$` would never match and the
 * NUL would survive into the UI. */
function trimDicomText(value) {
    const nul = value.indexOf('\0');
    return (nul === -1 ? value : value.slice(0, nul)).trim();
}
function decodeText(ds, el, decoder) {
    if (!decoder || !ds.byteArray || el.dataOffset === undefined)
        return null;
    const bytes = ds.byteArray.subarray(el.dataOffset, el.dataOffset + el.length);
    return trimDicomText(decoder.decode(bytes));
}
/**
 * Charset assumed for a dataset that declares no (0008,0005). DICOM's own
 * default repertoire is ASCII, but an object carrying bytes above 0x7F with no
 * declaration is non-conformant, and on the senders this actually happens on —
 * older German/Swiss DR/CR panels — those bytes are Latin-1. Decoding them as
 * Latin-1 matches what the backend does with the same objects
 * (`DicomTextEncoding.Fallback`), so the viewer and the worklist agree. It is
 * also lossless for the ASCII-only majority, which Latin-1 reproduces exactly.
 */
const ABSENT_CHARSET_LABEL = 'iso-8859-1';
let absentCharsetDecoder;
function decoderForAbsentCharset() {
    if (absentCharsetDecoder === undefined) {
        try {
            absentCharsetDecoder = new TextDecoder(ABSENT_CHARSET_LABEL);
        }
        catch {
            absentCharsetDecoder = null;
        }
    }
    return absentCharsetDecoder;
}
/**
 * Reads one top-level text element, honouring (0008,0005).
 *
 * This is the charset-correct replacement for `dataSet.string(tag)`, which
 * reads one byte per character whatever the object declares — so a conformant
 * `ISO_IR 192` study's "Müller" arrives as "MÃ¼ller". Use it for anything a
 * user reads: patient names, IDs, descriptions, institution, physician.
 *
 * Returns undefined when the element is absent or zero-length (both of which
 * `dataSet.string` also reports as undefined), and falls back to
 * `dataSet.string` when the declared charset is one TextDecoder can't handle
 * (multi-valued ISO 2022 code extensions) or the dataset carries no byteArray.
 */
export function readDecodedString(ds, tag) {
    const el = ds.elements?.[tag];
    if (!el || el.length === 0)
        return undefined;
    const decoded = decodeText(ds, el, resolveDecoder(ds, decoderForAbsentCharset()));
    if (decoded !== null)
        return decoded;
    const raw = ds.string?.(tag);
    return raw === undefined ? undefined : trimDicomText(raw);
}
function formatValue(ds, el, vr, hex, decoder) {
    if (el.length === 0)
        return { value: '(empty)', isBinary: false };
    // Compound dictionary VRs like 'OW|OB' — format by the first alternative.
    const primaryVr = vr.split('|')[0];
    if (BINARY_VRS.has(primaryVr) || hex === PIXEL_DATA_HEX) {
        return { value: binaryPlaceholder(el.length), isBinary: true };
    }
    if (primaryVr === 'AT') {
        const value = formatAttributeTagValue(ds, el);
        return value !== null
            ? { value, isBinary: false }
            : { value: binaryPlaceholder(el.length), isBinary: true };
    }
    const numeric = formatNumericValue(ds, el, primaryVr);
    if (numeric !== null)
        return { value: numeric, isBinary: false };
    if (NUMERIC_VRS[primaryVr]) {
        // Numeric VR but no reader on the dataset — show raw size, not garbage.
        return { value: binaryPlaceholder(el.length), isBinary: true };
    }
    if (CHARSET_TEXT_VRS.has(primaryVr)) {
        const decoded = decodeText(ds, el, decoder);
        if (decoded !== null)
            return { value: decoded, isBinary: false };
    }
    const str = ds.string?.(el.tag);
    if (str === undefined)
        return { value: binaryPlaceholder(el.length), isBinary: true };
    return { value: str, isBinary: false };
}
function truncate(row) {
    if (row.value.length > MAX_VALUE_LENGTH) {
        row.fullValue = row.value;
        row.value = `${row.value.slice(0, MAX_VALUE_LENGTH)}…`;
    }
}
export function enumerateDataSet(ds, dict) {
    const rows = [];
    walk(ds, dict, 0, '', rows, null);
    return rows;
}
function walk(ds, dict, depth, keyPrefix, rows, inheritedDecoder) {
    const decoder = resolveDecoder(ds, inheritedDecoder);
    const elements = Object.values(ds.elements).sort((a, b) => tagHex(a.tag).localeCompare(tagHex(b.tag)));
    for (const el of elements) {
        const hex = tagHex(el.tag);
        const dictEntry = dict[hex];
        const isPrivate = isPrivateGroup(hex);
        const vr = el.vr ?? dictEntry?.vr ?? 'UN';
        const key = keyPrefix + el.tag;
        const name = dictEntry?.name ?? '';
        if (vr === 'SQ' || el.items) {
            const items = el.items ?? [];
            rows.push({
                key,
                tag: displayTag(hex),
                vr: 'SQ',
                name,
                value: `<sequence, ${items.length} ${items.length === 1 ? 'item' : 'items'}>`,
                depth,
                isPrivate,
                isBinary: false,
                byteLength: el.length,
            });
            items.forEach((item, i) => {
                rows.push({
                    key: `${key}[${i}]`,
                    tag: displayTag('fffee000'),
                    vr: '',
                    name: `Item ${i + 1}`,
                    value: '',
                    depth: depth + 1,
                    isPrivate: false,
                    isBinary: false,
                    byteLength: 0,
                });
                if (item.dataSet)
                    walk(item.dataSet, dict, depth + 2, `${key}[${i}].`, rows, decoder);
            });
            continue;
        }
        const { value, isBinary } = formatValue(ds, el, vr, hex, decoder);
        const row = {
            key,
            tag: displayTag(hex),
            vr,
            name,
            value,
            depth,
            isPrivate,
            isBinary,
            byteLength: el.length,
        };
        truncate(row);
        rows.push(row);
    }
}
